import React, { useState, useRef } from "react";
import { ChevronRight, MapPin, Clock, Heart, HandshakeIcon, BookOpen, Music, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/FellowshipPage.module.css";
import heroBackground from "../assets/images/image8.png";
import welcomeImage from "../assets/images/img1.png";
import churchImage  from "../assets/images/church.png";
import churchImage1 from "../assets/images/img1.png";
import churchImage2 from "../assets/images/image7.png";
import churchImage3 from "../assets/images/image2.png";
import churchImage4 from "../assets/images/image3.png";
import churchImage5 from "../assets/images/image4.png";
import churchImage6 from "../assets/images/image5.png";
import EventNotificationPopup from "../components/EventNotificationPopup";

function useInView(ref, { threshold = 0.1, once = true } = {}) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); if (once) obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return visible;
}

const REGIONS = {
  Rwanda: [
    { name: "Kigali Main Parish",  address: "Kigali, Rwanda",    times: ["08:00 - 10:00","10:30 - 12:30","15:30 - 20:00"], special: "Main worship center with full amenities",   image: churchImage,  active: true  },
    { name: "Mbyo Parish",         address: "Mbyo, Rwanda",      times: ["09:00 - 11:00"],                                 special: "Community-focused worship",                image: churchImage1, active: true },
    { name: "Mbarara Parish",      address: "Mbarara, Rwanda",   times: ["10:00 - 12:00"],                                 special: "Youth-oriented services",                  image: churchImage2, active: true },
    { name: "Karangazi Parish",    address: "Karangazi, Rwanda", times: ["08:30 - 10:30"],                                 special: "Family-friendly environment",              image: churchImage3, active: true },
  ],
  DRC: [
    { name: "Bukavu Parish", address: "Bukavu, DRC", times: ["10:00 - 12:00"], special: "Cross-border ministry",          image: churchImage4, active: true },
    { name: "Goma Parish",   address: "Goma, DRC",   times: ["10:00 - 12:00"], special: "Community outreach programs",   image: churchImage5, active: true },
  ],
  Europe: [
    { name: "Norway Parish",  address: "Oslo, Norway",       times: ["10:00 - 12:00"], special: "Scandinavian ministry hub",    image: churchImage6, active: true },
    { name: "Belgium Parish", address: "Brussels, Belgium",  times: ["10:00 - 12:00"], special: "European worship center",     image: churchImage2, active: true },
  ],
  Diaspora: [
    { name: "Canada Parish", address: "Toronto, Canada", times: ["14:00 - 18:00"], special: "International worship community", image: churchImage3, active: true },
  ],
};

const EXPECTATIONS = [
  { icon: <HandshakeIcon size={28} />, title: "A Warm Welcome",                 desc: "When you step into our church, we hope you feel at home. We may all come from different backgrounds, but Sundays are a safe space to arrive as you are and be welcomed like family.",                         img: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop" },
  { icon: <BookOpen      size={28} />, title: "A Message From the Bible",       desc: "Our worship services revolve around Jesus. Each week you'll hear someone preach on a passage from the Bible that is meant to change us from the inside out.",                                                    img: "https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?w=800&h=600&fit=crop" },
  { icon: <Music         size={28} />, title: "A Time of Worship",              desc: "Every day we worship God through our actions, time, and resources. But on Sundays we get to worship Him with one another through song.",                                                                          img: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop" },
  { icon: <Users         size={28} />, title: "A Place to Connect With Others", desc: "You were made to be known by others. Sunday mornings are more than a weekly message — they're an opportunity to meet and begin to grow in community.",                                                         img: "https://images.unsplash.com/photo-1517457373614-b7152f800fd1?w=800&h=600&fit=crop" },
];

const FEATURES = [
  { icon: <Heart         size={44} />, title: "Spiritual Growth", desc: "Deepen your faith through inspiring messages, worship, and prayer with our community.",              img: "https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?w=800&h=600&fit=crop" },
  { icon: <HandshakeIcon size={44} />, title: "Community",        desc: "Build meaningful relationships with people who share your values and beliefs.",                 img: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop" },
  { icon: <Users         size={44} />, title: "Service",          desc: "Make a difference in the lives of others through various ministry opportunities.",            img: "https://images.unsplash.com/photo-1517457373614-b7152f800fd1?w=800&h=600&fit=crop" },
];

export default function FellowshipPage() {
  const navigate = useNavigate();
  const [activeRegion, setActiveRegion]           = useState("Rwanda");
  const [selectedExpectation, setSelectedExp]     = useState(0);
  const [selectedFeature, setSelectedFeature]     = useState(0);

  const handleScrollDown = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  const lightRef  = useRef(null);
  const expectRef = useRef(null);
  const ctaRef    = useRef(null);
  const lightVisible  = useInView(lightRef,  { threshold: 0.04 });
  const expectVisible = useInView(expectRef, { threshold: 0.05 });
  const ctaVisible    = useInView(ctaRef,    { threshold: 0.15 });

  return (
    <div className={styles.fellowshipPage}>

      {/* ── Cinematic Hero ── */}
      <section className={styles.hero}>
        <img src={heroBackground} alt="" className={styles.heroImg} />
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <span className={styles.eyebrow}>CWFM · Locations &amp; Fellowship</span>
          <h1 className={styles.heroTitle}>Join Us<br />This Sunday</h1>
          <div className={styles.titleBar} />
          <p className={styles.heroSub}>
            Experience authentic worship, hear God's Word, and connect with a
            community that cares about you — across Rwanda, DRC, Europe &amp; beyond.
          </p>
          <div className={styles.heroBtns}>
            <button className={styles.btnGold} onClick={() => document.getElementById("locations")?.scrollIntoView({ behavior:"smooth" })}>
              Find a Location
            </button>
            <button className={styles.btnOutline} onClick={() => navigate("/membership")}>
              Join Our Community
            </button>
          </div>
        </div>
        <EventNotificationPopup />
        <button
          className={styles.scrollCue}
          onClick={handleScrollDown}
          aria-label="Scroll down"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </section>

      {/* ── Light Section (Welcome + Features + Locations) ── */}
      <div className={styles.lightOuter}>
        <div
          ref={lightRef}
          className={`${styles.lightInner} ${lightVisible ? styles.lightVisible : ""}`}
        >

          {/* Welcome */}
          <div className={styles.welcomeGrid}>
            <div className={styles.welcomeImgWrap}>
              <img src={welcomeImage} alt="Calvary Wide Fellowship Community" className={styles.welcomeImg} />
            </div>
            <div className={styles.welcomeCard}>
              <span className={styles.sectionEyebrow}>Welcome to CWFM</span>
              <h2 className={styles.welcomeTitle}>
                We're a New Testament church existing for the supremacy of the name and purpose of
                Jesus Christ. We believe Christ's life, death, and resurrection are essential to
                God's plan to heal and redeem our fallen world.
              </h2>
              <div className={styles.sectionTitleBar} />
              <button className={styles.btnBlue} onClick={() => navigate("/about")}>
                Learn More <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Why Join */}
          <div className={styles.featuresWrap}>
            <div className={styles.featuresHead}>
              <span className={styles.sectionEyebrow}>Why Join Us</span>
              <h2 className={styles.sectionTitle}>Faith, Community &amp; Service</h2>
            </div>
            <div className={styles.featuresGrid}>
              {FEATURES.map((f, i) => (
                <div
                  key={i}
                  className={`${styles.featureCard} ${selectedFeature === i ? styles.featureCardActive : ""}`}
                  onMouseEnter={() => setSelectedFeature(i)}
                  onClick={() => setSelectedFeature(i)}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className={styles.featureImgBg} style={{ backgroundImage: `url(${f.img})` }} />
                  <div className={styles.featureCardOverlay} />
                  <div className={styles.featureCardBody}>
                    <div className={styles.featureIconWrap}>{f.icon}</div>
                    <h3 className={styles.featureCardTitle}>{f.title}</h3>
                    <p className={styles.featureCardDesc}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Locations */}
          <div id="locations" className={styles.locWrap}>
            <div className={styles.locHead}>
              <span className={styles.sectionEyebrow}>Multiple Locations</span>
              <h2 className={styles.sectionTitle}>One Church, Many Communities</h2>
              <p className={styles.sectionDesc}>
                Calvary Wide Fellowship is one church with multiple congregations across four regions.
              </p>
            </div>
            <div className={styles.regionTabs}>
              {Object.keys(REGIONS).map((r) => (
                <button
                  key={r}
                  className={`${styles.regionTab} ${activeRegion === r ? styles.regionTabActive : ""}`}
                  onClick={() => setActiveRegion(r)}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className={styles.locGrid}>
              {REGIONS[activeRegion].map((loc, i) => (
                <div key={i} className={`${styles.locCard} ${!loc.active ? styles.locCardInactive : ""}`}>
                  <div className={styles.locImgWrap}>
                    <img src={loc.image} alt={loc.name} className={styles.locImg} />
                    <div className={styles.locImgOverlay} />
                    <span className={`${styles.locBadge} ${loc.active ? styles.locBadgeActive : styles.locBadgeInactive}`}>
                      {loc.active ? "Active" : "Coming Soon"}
                    </span>
                  </div>
                  <div className={styles.locBody}>
                    <h3 className={styles.locName}>{loc.name}</h3>
                    {loc.special && <span className={styles.locSpecial}>{loc.special}</span>}
                    <div className={styles.locInfoRow}>
                      <MapPin size={15} className={styles.locInfoIcon} />
                      <span>{loc.address}</span>
                    </div>
                    {loc.times && loc.times.length > 0 && (
                      <div className={styles.locInfoRow}>
                        <Clock size={15} className={styles.locInfoIcon} />
                        <div className={styles.locTimes}>
                          {loc.times.map((t, ti) => (
                            <span key={ti} className={styles.timeSlot}>{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── What to Expect (dark) ── */}
      <section
        ref={expectRef}
        className={`${styles.expectSection} ${expectVisible ? styles.expectVisible : ""}`}
      >
        <div className={styles.expectInner}>
          <div className={styles.expectHead}>
            <span className={styles.expectEyebrow}>Sunday Experience</span>
            <h2 className={styles.expectTitle}>What to Expect</h2>
            <div className={styles.expectTitleBar} />
            <p className={styles.expectDesc}>You belong here</p>
          </div>
          <div className={styles.expectGrid}>
            {EXPECTATIONS.map((item, i) => (
              <div
                key={i}
                className={`${styles.expectCard} ${selectedExpectation === i ? styles.expectCardActive : ""}`}
                onMouseEnter={() => setSelectedExp(i)}
                onClick={() => setSelectedExp(i)}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className={styles.expectImgBg} style={{ backgroundImage: `url(${item.img})` }} />
                <div className={styles.expectCardOverlay} />
                <div className={styles.expectCardBody}>
                  <div className={styles.expectIconWrap}>{item.icon}</div>
                  <h3 className={styles.expectCardTitle}>{item.title}</h3>
                  <p className={styles.expectCardDesc}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA (dark) ── */}
      <section
        ref={ctaRef}
        className={`${styles.ctaSection} ${ctaVisible ? styles.ctaVisible : ""}`}
        style={{ backgroundImage: `url(${heroBackground})` }}
      >
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaContent}>
          <span className={styles.ctaEyebrow}>Ready to Take the Next Step?</span>
          <h2 className={styles.ctaTitle}>Join Our Fellowship</h2>
          <div className={styles.ctaTitleBar} />
          <p className={styles.ctaDesc}>
            Take the first step towards a meaningful spiritual journey with our community.
          </p>
          <div className={styles.ctaBtns}>
            <button className={styles.ctaBtnGold} onClick={() => document.getElementById("locations")?.scrollIntoView({ behavior:"smooth" })}>
              Visit This Sunday
            </button>
            <button className={styles.ctaBtnOutline} onClick={() => navigate("/membership")}>
              Learn More
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
